import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';

import {
  LOCALES,
  LANGUAGES,
  DEFAULT_LANG,
  isSupported,
  detectLanguage,
  writeStored,
  translate,
} from './core';

// Re-exported so app code and tests can keep importing from '../i18n'.
export { LOCALES, LANGUAGES, DEFAULT_LANG, isSupported, detectLanguage, translate };

const I18nContext = createContext(null);

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(detectLanguage);

  useEffect(() => {
    // Screen readers and browser translation prompts both key off this.
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((code) => {
    if (!isSupported(code)) return;
    setLangState(code);
    writeStored(code);
  }, []);

  const t = useCallback((key, vars) => translate(lang, key, vars), [lang]);

  const value = useMemo(() => ({ lang, setLang, t, languages: LANGUAGES }), [lang, setLang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Keeps isolated component tests and any stray usage from crashing.
    return {
      lang: DEFAULT_LANG,
      setLang: () => {},
      t: (key, vars) => translate(DEFAULT_LANG, key, vars),
      languages: LANGUAGES,
    };
  }
  return ctx;
};
